// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel';

import { LocalTimeSlot } from './LocalTimeSlot';
import { LatLng } from './LatLng';
import { LatLngLineSegments, CompositeLatLngPolygon } from './Geometry';

import type { LatLngModelType } from './LatLng';
import type { LocalTimeSlotModelType } from './LocalTimeSlot';
import type {
    LatLngLineSegmentsModelType,
    CompositeLatLngPolygonModelType
} from './Geometry';
export type RouteRestrictionTypeEnum = 'ROAD' | 'REGION' | 'DIVIDE';
export type RouteRestrictionEnforcementTypeEnum = 'STRICT' | 'HIGH_TRAFFIC';
export type DaysTypeEnum =
    | 'SUNDAY'
    | 'MONDAY'
    | 'TUESDAY'
    | 'WEDNESDAY'
    | 'THURSDAY'
    | 'FRIDAY'
    | 'SATURDAY';

export type RouteRestrictionModelType = {
    id: string,
    center: LatLngModelType,
    name: string,
    enforcement: RouteRestrictionEnforcementTypeEnum,
    trafficFactor: ?number,
    type: RouteRestrictionTypeEnum,
    polyline: ?string,
    polygon: ?CompositeLatLngPolygonModelType,
    timeSlots: Array<LocalTimeSlotModelType>,
    vehicleModelIds: Array<VehicleModelIdModelType>,
    vehicleTypes: Array<string>,
    vehicleSkills: Array<string>,
    lineSegments: ?LatLngLineSegmentsModelType,
    restrictedDaysMap: RestrictedDaysMapModelType,
    restrictedDatesMap: RestrictedDaysMapModelType,
    vehicleNumbers: Array<string>,
    daysForRestrictedDates: Array<DaysTypeEnum>
};

export type VehicleModelIdModelType = {
    clientId: string,
    modelId: string
};

export type RestrictedDaysMapModelType = {
    [key: string]: string
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class RouteRestriction extends ImmutableModel {
    static fromJS(json: RouteRestrictionModelType): RouteRestriction {
        const state: Object = Object.assign({}, json);
        state.center = LatLng.fromJS(state.center);
        state.polygon = state.polygon
            ? CompositeLatLngPolygon.fromJS(state.polygon)
            : state.polygon;
        state.timeSlots = Immutable.List(state.timeSlots).map(item =>
            LocalTimeSlot.fromJS(item)
        );
        state.vehicleModelIds = Immutable.List(
            state.vehicleModelIds
        ).map(item => VehicleModelId.fromJS(item));
        state.vehicleTypes = Immutable.List(state.vehicleTypes);
        state.vehicleSkills = Immutable.List(state.vehicleSkills);
        state.lineSegments = state.lineSegments
            ? LatLngLineSegments.fromJS(state.lineSegments)
            : state.lineSegments;
        state.restrictedDaysMap = RestrictedDaysMap.fromJS(
            state.restrictedDaysMap
        );
        state.restrictedDatesMap = RestrictedDaysMap.fromJS(
            state.restrictedDatesMap
        );
        state.vehicleNumbers = Immutable.List(state.vehicleNumbers);
        state.daysForRestrictedDates = Immutable.List(
            state.daysForRestrictedDates
        );
        return new this(Immutable.Map(state));
    }

    toJS(): RouteRestrictionModelType {
        return {
            id: this.id,
            center: this.center.toJS(),
            name: this.name,
            enforcement: this.enforcement,
            trafficFactor: this.trafficFactor,
            type: this.type,
            polyline: this.polyline,
            polygon: this.polygon ? this.polygon.toJS() : this.polygon,
            timeSlots: this.timeSlots.toArray().map(item => item.toJS()),
            vehicleModelIds: this.vehicleModelIds
                .toArray()
                .map(item => item.toJS()),
            vehicleTypes: this.vehicleTypes.toArray(),
            vehicleSkills: this.vehicleSkills.toArray(),
            lineSegments: this.lineSegments
                ? this.lineSegments.toJS()
                : this.lineSegments,
            restrictedDaysMap: this.restrictedDaysMap.toJS(),
            restrictedDatesMap: this.restrictedDatesMap.toJS(),
            vehicleNumbers: this.vehicleNumbers.toArray(),
            daysForRestrictedDates: this.daysForRestrictedDates.toArray()
        };
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get center(): LatLng {
        return this._state.get('center');
    }

    setCenter(center: LatLng): this {
        return this.clone(this._state.set('center', center));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get enforcement(): RouteRestrictionEnforcementTypeEnum {
        return this._state.get('enforcement');
    }

    setEnforcement(enforcement: RouteRestrictionEnforcementTypeEnum): this {
        return this.clone(this._state.set('enforcement', enforcement));
    }

    get trafficFactor(): ?number {
        return this._state.get('trafficFactor');
    }

    setTrafficFactor(trafficFactor: ?number): this {
        return this.clone(this._state.set('trafficFactor', trafficFactor));
    }

    get type(): RouteRestrictionTypeEnum {
        return this._state.get('type');
    }

    setType(type: RouteRestrictionTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }

    get polyline(): ?string {
        return this._state.get('polyline');
    }

    setPolyline(polyline: ?string): this {
        return this.clone(this._state.set('polyline', polyline));
    }

    get polygon(): ?CompositeLatLngPolygon {
        return this._state.get('polygon');
    }

    setPolygon(polygon: ?CompositeLatLngPolygon): this {
        return this.clone(this._state.set('polygon', polygon));
    }

    get timeSlots(): Immutable.List<LocalTimeSlot> {
        return this._state.get('timeSlots');
    }

    setTimeSlots(timeSlots: Immutable.List<LocalTimeSlot>): this {
        return this.clone(this._state.set('timeSlots', timeSlots));
    }

    get vehicleModelIds(): Immutable.List<VehicleModelId> {
        return this._state.get('vehicleModelIds');
    }

    setVehicleModelIds(vehicleModelIds: Immutable.List<VehicleModelId>): this {
        return this.clone(this._state.set('vehicleModelIds', vehicleModelIds));
    }

    get vehicleTypes(): Immutable.List<string> {
        return this._state.get('vehicleTypes');
    }

    setVehicleTypes(vehicleTypes: Immutable.List<string>): this {
        return this.clone(this._state.set('vehicleTypes', vehicleTypes));
    }

    get vehicleSkills(): Immutable.List<string> {
        return this._state.get('vehicleSkills');
    }

    setVehicleSkills(vehicleSkills: Immutable.List<string>): this {
        return this.clone(this._state.set('vehicleSkills', vehicleSkills));
    }

    get lineSegments(): ?LatLngLineSegments {
        return this._state.get('lineSegments');
    }

    setLineSegments(lineSegments: ?LatLngLineSegments): this {
        return this.clone(this._state.set('lineSegments', lineSegments));
    }

    get restrictedDaysMap(): RestrictedDaysMap {
        return this._state.get('restrictedDaysMap');
    }

    setRestrictedDaysMap(restrictedDaysMap: RestrictedDaysMap): this {
        return this.clone(
            this._state.set('restrictedDaysMap', restrictedDaysMap)
        );
    }

    get restrictedDatesMap(): RestrictedDaysMap {
        return this._state.get('restrictedDatesMap');
    }

    setRestrictedDatesMap(restrictedDatesMap: RestrictedDaysMap): this {
        return this.clone(
            this._state.set('restrictedDatesMap', restrictedDatesMap)
        );
    }

    get vehicleNumbers(): Immutable.List<string> {
        return this._state.get('vehicleNumbers');
    }

    setVehicleNumbers(vehicleNumbers: Immutable.List<string>): this {
        return this.clone(this._state.set('vehicleNumbers', vehicleNumbers));
    }

    get daysForRestrictedDates(): Immutable.List<DaysTypeEnum> {
        return this._state.get('daysForRestrictedDates');
    }

    setDaysForRestrictedDates(
        daysForRestrictedDates: Immutable.List<DaysTypeEnum>
    ): this {
        return this.clone(
            this._state.set('daysForRestrictedDates', daysForRestrictedDates)
        );
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class VehicleModelId extends ImmutableModel {
    static fromJS(json: VehicleModelIdModelType): VehicleModelId {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): VehicleModelIdModelType {
        return {
            clientId: this.clientId,
            modelId: this.modelId
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get modelId(): string {
        return this._state.get('modelId');
    }

    setModelId(modelId: string): this {
        return this.clone(this._state.set('modelId', modelId));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class RestrictedDaysMap extends ImmutableModel {
    static fromJS(json: RestrictedDaysMapModelType): RestrictedDaysMap {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): RestrictedDaysMapModelType {
        return {};
    }
}
